import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangleIcon, DownloadIcon, PrinterIcon, RotateCwIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { useData } from '../../store/DataContext';
import { PurchaseOrderLine } from '../../types';
import { formatCurrency } from './pharmacyUtils';
type PurchaseOrderDocumentProps = {
  orderId: string;
  onClose: () => void;
};
type NumberedLine = PurchaseOrderLine & {
  index: number;
};
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png';
/** OCR A Extended when installed (ships with MS Office), then OCR-A variants, then a bold monospace fallback. */
const SERIAL_FONT = "'OCR A Extended', 'OCR A Std', 'OCR-A', 'OCR A', 'Courier New', Courier, monospace";
// Rows that fit on an A4 page: the first page carries the full letterhead.
const FIRST_PAGE_ROWS = 14;
const NEXT_PAGE_ROWS = 24;
// Vertical room the totals + signature block needs, measured in table rows.
const SUMMARY_ROW_EQUIVALENT = 9;
export function PurchaseOrderDocument({
  orderId,
  onClose
}: PurchaseOrderDocumentProps) {
  const {
    purchaseOrders,
    suppliers,
    ensurePurchaseOrderSerial
  } = useData();
  const order = purchaseOrders.find(item => item.id === orderId);
  const supplier = suppliers.find(item => item.id === order?.supplierId);
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [serialState, setSerialState] = useState<'idle' | 'assigning' | 'error'>('idle');
  const [serialError, setSerialError] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const assignSerial = useCallback(async () => {
    setSerialState('assigning');
    setSerialError('');
    const result = await ensurePurchaseOrderSerial(orderId);
    if (result.ok) {
      setSerialState('idle');
    } else {
      setSerialState('error');
      setSerialError(result.error || 'The serial number could not be assigned.');
    }
  }, [ensurePurchaseOrderSerial, orderId]);

  // Orders created before serial numbering get theirs on first open — once.
  useEffect(() => {
    if (order && !order.serialNumber && serialState === 'idle') {
      void assignSerial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, order?.serialNumber]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  if (!order) return null;
  const serialNumber = order.serialNumber;
  const isReady = Boolean(serialNumber);
  const numberedLines: NumberedLine[] = order.lines.map((line, index) => ({
    ...line,
    index: index + 1
  }));
  const pages = paginate(numberedLines);
  const lineTotal = (line: PurchaseOrderLine) => line.quantityOrdered * line.unitCost;
  const grandTotal = order.totalCost ?? order.lines.reduce((sum, line) => sum + lineTotal(line), 0);
  const totalQuantity = order.lines.reduce((sum, line) => sum + line.quantityOrdered, 0);
  const issueDate = formatLongDate(order.createdAt || order.orderedAt);
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!serialNumber) return;
    setIsGeneratingPdf(true);
    setPdfError('');
    try {
      const nodes = pageRefs.current.slice(0, pages.length).filter((node): node is HTMLDivElement => Boolean(node));
      if (nodes.length === 0) throw new Error('Nothing to render');
      await document.fonts?.ready;
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      for (let i = 0; i < nodes.length; i += 1) {
        const canvas = await html2canvas(nodes[i], {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false
        });
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, 210, 297);
      }
      pdf.save(`${serialNumber}_${sanitizeFilename(order.supplierName || 'Supplier')}.pdf`);
    } catch (error) {
      console.error('Purchase order PDF generation failed', error);
      setPdfError('The purchase order PDF could not be generated. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="purchase-order-document-title" data-id="element-9421" data-mp="99ci804s3vj8">
      <div className="test-modal-print flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-slate-100 shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:bg-white print:shadow-none" data-id="element-9422" data-mp="3cptabpslbrvl">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-9423" data-mp="1j9yels3s8j3z">
          <div data-id="element-9424" data-mp="1n8fmweyxvzmz">
            <h2 id="purchase-order-document-title" className="text-lg font-bold text-slate-900" data-id="element-9425" data-mp="1n85ygvtjm0je">
              Purchase order {serialNumber ?? order.orderNumber}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-9426" data-mp="21zogez5yq55e">
              A4 document on BMED Medical Centre letterhead
              {pages.length > 1 ? ` · ${pages.length} pages` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-id="element-9427" data-mp="qjq2agvxgsxb">
            <Button variant="outline" size="sm" onClick={handlePrint} disabled={!isReady} data-id="element-9428" data-mp="qmm3qf6zhzqi">
              <PrinterIcon className="mr-1.5 h-4 w-4" data-id="element-9429" data-mp="2x76zwx2bl35r" /> Print
            </Button>
            <Button size="sm" onClick={handleDownloadPdf} disabled={!isReady || isGeneratingPdf} isLoading={isGeneratingPdf} className="bg-teal-600 hover:bg-teal-700 focus:ring-teal-500" data-id="element-9430" data-mp="2s95ishogfdwx">
              {!isGeneratingPdf && <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-9431" data-mp="2eiaeliowhkzo" />}
              {isGeneratingPdf ? 'Generating PDF…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500" aria-label="Close purchase order" data-id="element-9432" data-mp="29l0hdgv9s51a">
              <XIcon className="h-5 w-5" data-id="element-9433" data-mp="2r6hiyluha6p" />
            </button>
          </div>
        </header>

        {serialState === 'assigning' && <p role="status" className="border-b border-slate-200 bg-white px-5 py-2 text-sm text-slate-600 print:hidden" data-id="element-9434" data-mp="3uufqh6h6h2em">
            Assigning the official purchase order serial number…
          </p>}
        {serialState === 'error' && <div role="alert" className="flex flex-wrap items-center gap-3 border-b border-red-200 bg-red-50 px-5 py-2 text-sm text-red-800 print:hidden" data-id="element-9435" data-mp="1qf40h6cv7y5x">
            <AlertTriangleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-9436" data-mp="xr809ro37o4e" />
            <span className="flex-1" data-id="element-9437" data-mp="12pj6ztj35iie">
              {serialError} The document can't be issued without a serial
              number.
            </span>
            <button type="button" onClick={() => void assignSerial()} className="inline-flex items-center gap-1 font-semibold text-red-800 underline-offset-2 hover:underline" data-id="element-9438" data-mp="1bpep6ib26drx">
              <RotateCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-9439" data-mp="2n3s776j4rz01" /> Retry
            </button>
          </div>}
        {pdfError && <div role="alert" className="flex flex-wrap items-center gap-3 border-b border-red-200 bg-red-50 px-5 py-2 text-sm text-red-800 print:hidden" data-id="element-9440" data-mp="1ypx0oeh5aa3l">
            <span className="flex-1" data-id="element-9441" data-mp="27lmfg4o38680">{pdfError}</span>
            <button type="button" onClick={() => void handleDownloadPdf()} className="inline-flex items-center gap-1 font-semibold text-red-800 underline-offset-2 hover:underline" data-id="element-9442" data-mp="22t32n9x85f49">
              <RotateCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-9443" data-mp="2ciwd27lmro6x" /> Retry
            </button>
          </div>}

        <div className="flex-1 overflow-auto p-4 sm:p-6 print:overflow-visible print:p-0" data-id="element-9444" data-mp="2qh32czcikfuz">
          <div className="mx-auto flex w-[210mm] flex-col gap-6 print:w-auto print:gap-0" data-id="element-9445" data-mp="2midl2vocue62">
            {pages.map((pageLines, pageIndex) => {
            const isFirst = pageIndex === 0;
            const isLast = pageIndex === pages.length - 1;
            return <div key={pageIndex} ref={node => {
              pageRefs.current[pageIndex] = node;
            }} className="flex h-[297mm] w-[210mm] flex-col bg-white px-[14mm] py-[12mm] text-[12px] leading-snug text-slate-900 shadow-md print:h-auto print:min-h-0 print:w-auto print:break-after-page print:p-0 print:shadow-none print:last:break-after-auto" data-id="element-9446" data-mp="i0xh1ksfiujd" data-mpi={pageIndex}>
                  {isFirst ? <>
                      <header className="flex items-start justify-between gap-6 border-b-2 border-slate-900 pb-4" data-id="element-9447" data-mp="3cb4try3miwd">
                        <div className="flex items-center gap-4" data-id="element-9448" data-mp="3li7dy5vn36b8">
                          <img src={LOGO_URL} alt="BMED Medical Centre" crossOrigin="anonymous" className="h-20 w-20 shrink-0 object-contain" data-id="element-9449" data-mp="3qgh607m2hqsm" />
                          <div data-id="element-9450" data-mp="2uzll0ny0snqt">
                            <p className="text-2xl font-bold tracking-wide text-pink-600" data-id="element-9451" data-mp="2uyaj6to4xq2c">
                              BMED MEDICAL CENTRE
                            </p>
                            <p className="italic text-slate-600" data-id="element-9452" data-mp="2lzyl66kxvpzv">
                              Caring Healing Leading
                            </p>
                          </div>
                        </div>
                        <address className="text-right text-[11px] not-italic leading-relaxed text-slate-700" data-id="element-9453" data-mp="3drms2dky7kbb">
                          <p data-id="element-9454" data-mp="3iowdzj94pdue">Plot No. 1884/10, Along Avic Road, Lilayi Estate</p>
                          <p data-id="element-9455" data-mp="34olnihbqe19r">Kafue, Lusaka - Zambia</p>
                          <p data-id="element-9456" data-mp="39oq3m68q8tzg">Contact: +260975202011</p>
                          <p data-id="element-9457" data-mp="39neyo33jpg6r">Email: bmedhospital@gmail.com</p>
                          <p data-id="element-9458" data-mp="1t9ymiph1795g">TPIN: 2000869447</p>
                        </address>
                      </header>

                      <div className="mt-5 flex items-end justify-between gap-6" data-id="element-9459" data-mp="1y9upqmhwmwjy">
                        <div data-id="element-9460" data-mp="1psao2c1i1eny">
                          <h3 className="text-xl font-bold uppercase tracking-wider text-slate-900" data-id="element-9461" data-mp="1pr993whd480y">
                            Purchase Order
                          </h3>
                          <p className="mt-0.5 text-slate-500" data-id="element-9462" data-mp="1zr9vjraj349d">
                            Ref. {order.orderNumber}
                            {order.status === 'Cancelled' ? '' : ` · ${order.status}`}
                          </p>
                        </div>
                        <dl className="text-right" data-id="element-9463" data-mp="124avr7ojhg79">
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-9464" data-mp="1grvz92pbthfp">
                            Serial No.
                          </dt>
                          <dd className="text-2xl" style={{
                      color: '#DC2626',
                      fontWeight: 700,
                      fontFamily: SERIAL_FONT,
                      letterSpacing: '0.04em'
                    }} data-id="element-9465" data-mp="1bz2uietuy93m">
                            {serialNumber ?? '— pending —'}
                          </dd>
                          <dt className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-9466" data-mp="2t5ud20hig8gp">
                            Date
                          </dt>
                          <dd className="font-semibold" data-id="element-9467" data-mp="20gnhnadcryzo">{issueDate}</dd>
                        </dl>
                      </div>

                      {order.status === 'Cancelled' && <p className="mt-4 border-2 border-red-600 px-3 py-2 text-center font-bold uppercase tracking-wider text-red-700" data-id="element-9468" data-mp="29g26y6hsa3il">
                          Cancelled
                          {order.cancelledAt ? ` on ${formatLongDate(order.cancelledAt)}` : ''}
                          {order.cancellationReason ? ` — ${order.cancellationReason}` : ''}
                        </p>}

                      <div className="mt-5 grid grid-cols-2 gap-6" data-id="element-9469" data-mp="3v0ysw2kxgowl">
                        <section data-id="element-9470" data-mp="3q8fe52ai7nq7">
                          <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-9471" data-mp="3q7mdmj251ugi">
                            Supplier
                          </h4>
                          <p className="font-bold" data-id="element-9472" data-mp="3h9afzx2utwek">
                            {supplier?.name || order.supplierName || '—'}
                          </p>
                          {supplier?.contactPerson && <p data-id="element-9473" data-mp="37aus02z4j39a">Attn: {supplier.contactPerson}</p>}
                          {supplier?.address && <p data-id="element-9474" data-mp="33aaswtuzppki">{supplier.address}</p>}
                          {supplier?.phone && <p data-id="element-9475" data-mp="15hoq6qcchpif">Tel: {supplier.phone}</p>}
                          {supplier?.email && <p data-id="element-9476" data-mp="11glaq4y7lvhq">Email: {supplier.email}</p>}
                        </section>
                        <section data-id="element-9477" data-mp="18kkrb6a9hswm">
                          <h4 className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-9478" data-mp="18lckybkatgk9">
                            Order details
                          </h4>
                          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5" data-id="element-9479" data-mp="zkek2255pp5w">
                            <dt className="text-slate-500" data-id="element-9480" data-mp="13kovhubpx7s5">Order date</dt>
                            <dd data-id="element-9481" data-mp="pttlgephgkmz">{formatLongDate(order.orderedAt)}</dd>
                            <dt className="text-slate-500" data-id="element-9482" data-mp="psik0ljifoz1">Expected delivery</dt>
                            <dd data-id="element-9483" data-mp="ulbmf2rial7y">
                              {order.expectedDate ? formatLongDate(`${order.expectedDate}T00:00:00`) : '—'}
                            </dd>
                            <dt className="text-slate-500" data-id="element-9484" data-mp="ksdyui7nq62h">Deliver to</dt>
                            <dd data-id="element-9485" data-mp="6vr32irfxly5">BMED Medical Centre Pharmacy</dd>
                          </dl>
                        </section>
                      </div>
                    </> : <header className="flex items-center justify-between border-b border-slate-300 pb-2" data-id="element-9486" data-mp="3odwo86tbl98a">
                      <div className="flex items-center gap-2" data-id="element-9487" data-mp="3jgo3bygser3c">
                        <img src={LOGO_URL} alt="" crossOrigin="anonymous" className="h-8 w-8 object-contain" data-id="element-9488" data-mp="19zx25a03xw3" />
                        <span className="font-bold text-pink-600" data-id="element-9489" data-mp="b51mz3c3p3zq">
                          BMED MEDICAL CENTRE
                        </span>
                        <span className="text-slate-500" data-id="element-9490" data-mp="kx7ds9qm9fdc">
                          · Purchase Order (continued)
                        </span>
                      </div>
                      <span style={{
                  color: '#DC2626',
                  fontWeight: 700,
                  fontFamily: SERIAL_FONT
                }} data-id="element-9491" data-mp="txm3dlam5gmn">
                        {serialNumber}
                      </span>
                    </header>}

                  {(pageLines.length > 0 || isFirst && order.lines.length === 0) && <table className="mt-5 w-full border-collapse" data-id="element-9492" data-mp="3rpmgcdplzinf">
                      <thead data-id="element-9493" data-mp="crsi7e93uz9">
                        <tr className="bg-slate-900 text-left text-[10px] uppercase tracking-wider text-white" data-id="element-9494" data-mp="e2vw63sb0pu">
                          <th scope="col" className="w-8 px-2 py-2" data-id="element-9495" data-mp="5bcvgekw0sre">#</th>
                          <th scope="col" className="px-2 py-2" data-id="element-9496" data-mp="a9eb0pj3u9y4">Item / Drug name</th>
                          <th scope="col" className="px-2 py-2 text-right" data-id="element-9497" data-mp="f9ir4eg3p2nt">Qty</th>
                          <th scope="col" className="px-2 py-2 text-right" data-id="element-9498" data-mp="jbvzzbq59got">Unit price</th>
                          <th scope="col" className="px-2 py-2 text-right" data-id="element-9499" data-mp="2h1f31p66kej4">Total</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-9500" data-mp="2l3iieovz039o">
                        {pageLines.length === 0 ? <tr data-id="element-9501" data-mp="2dk9jjpl3bmyv">
                            <td colSpan={5} className="px-2 py-4 text-center text-slate-500" data-id="element-9502" data-mp="2dkj1cpvzenth">
                              No items on this order.
                            </td>
                          </tr> : pageLines.map(line => <tr key={`${line.medicineId}-${line.index}`} className="border-b border-slate-200 even:bg-slate-50" data-id="element-9503" data-mp="1ztnypvc2t2yc">
                              <td className="px-2 py-1.5 text-slate-500" data-id="element-9504" data-mp="2rh68flhneud4">{line.index}</td>
                              <td className="px-2 py-1.5 font-medium" data-id="element-9505" data-mp="2njibkcwearil">{line.medicineName}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums" data-id="element-9506" data-mp="2ii1d0o1dvh56">
                                {line.quantityOrdered.toLocaleString()}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums" data-id="element-9507" data-mp="171ku2kqmeidk">
                                {formatCurrency(line.unitCost)}
                              </td>
                              <td className="px-2 py-1.5 text-right font-medium tabular-nums" data-id="element-9508" data-mp="3b7s20fr59pnz">
                                {formatCurrency(lineTotal(line))}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>}

                  {isLast && <div className="mt-4 break-inside-avoid" data-id="element-9509" data-mp="3uty4k6vg64ja">
                      <div className="flex justify-end" data-id="element-9510" data-mp="3g63aam1sjs43">
                        <dl className="w-64 border-t-2 border-slate-900 pt-2" data-id="element-9511" data-mp="3g5k3a0doio4u">
                          <div className="flex justify-between text-slate-600" data-id="element-9512" data-mp="3l44uxbev7zho">
                            <dt data-id="element-9513" data-mp="couvkb2d18dl">Items</dt>
                            <dd className="tabular-nums" data-id="element-9514" data-mp="co1xrzldpt7l">
                              {order.lines.length} · {totalQuantity.toLocaleString()} units
                            </dd>
                          </div>
                          <div className="mt-1 flex justify-between text-base font-bold" data-id="element-9515" data-mp="224dwi353d0g0">
                            <dt data-id="element-9516" data-mp="1x75ga83i76he">Grand total</dt>
                            <dd className="tabular-nums" data-id="element-9517" data-mp="2azbtpmf9uzd5">{formatCurrency(grandTotal)}</dd>
                          </div>
                        </dl>
                      </div>

                      {order.notes && <section className="mt-4" data-id="element-9518" data-mp="1j8nu4et8y21l">
                          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-9519" data-mp="1j9h2tlbrn1md">
                            Notes
                          </h4>
                          <p className="mt-1 whitespace-pre-line" data-id="element-9520" data-mp="1s9m2taemqxqi">{order.notes}</p>
                        </section>}

                      <section aria-label="Pharmacist signature" className="signature-block no-break mt-10 w-[60%] space-y-5" data-id="element-9521" data-mp="33n7qer4ucr9x">
                        <p className="font-semibold" data-id="element-9522" data-mp="2oeqxstd04kzd">
                          Prepared By: <span className="font-normal" data-id="element-9523" data-mp="2oh3fr6irevcf">Pharmacist</span>
                        </p>
                        {['Name', 'Signature', 'Date'].map(label => <div key={label} className="flex items-end gap-2" data-id="element-9524" data-mp="2ydyf1rmuwzrr">
                            <span className="w-20 shrink-0" data-id="element-9525" data-mp="2tdu2gcp2kp6t">{label}:</span>
                            <span className="h-5 flex-1 border-b border-slate-800" data-id="element-9526" data-mp="37dlg2cboc3ka" />
                          </div>)}
                      </section>
                    </div>}

                  <footer className="mt-auto flex justify-between border-t border-slate-200 pt-2 text-[10px] text-slate-500" data-id="element-9527" data-mp="3h3xvxr8jn8k3">
                    <span data-id="element-9528" data-mp="3c53g8y5vi047">
                      {serialNumber ?? order.orderNumber} · Issued {issueDate}
                    </span>
                    <span data-id="element-9529" data-mp="3q2036oivexft">
                      Page {pageIndex + 1} of {pages.length}
                    </span>
                  </footer>
                </div>;
          })}
          </div>
        </div>
      </div>
    </div>;
}

/** Splits lines across A4 pages; adds a final page if totals + signature won't fit. */
function paginate(lines: NumberedLine[]): NumberedLine[][] {
  const pages: NumberedLine[][] = [];
  let cursor = 0;
  let capacity = FIRST_PAGE_ROWS;
  while (cursor < lines.length) {
    pages.push(lines.slice(cursor, cursor + capacity));
    cursor += capacity;
    capacity = NEXT_PAGE_ROWS;
  }
  if (pages.length === 0) return [[]];
  const lastCapacity = pages.length === 1 ? FIRST_PAGE_ROWS : NEXT_PAGE_ROWS;
  if (pages[pages.length - 1].length > lastCapacity - SUMMARY_ROW_EQUIVALENT) {
    pages.push([]);
  }
  return pages;
}
function formatLongDate(value?: string) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
}
function sanitizeFilename(value: string) {
  return value.replace(/[^a-z0-9-_]+/gi, '_').replace(/^_+|_+$/g, '');
}